import { LEVELS, Level } from "../../../types";
import { Field, inputClass } from "./Shell";

// Capture-list filters, mirrored into the URL hash so a view is linkable.
export type CaptureFilters = {
  level?: string; // comma list
  source?: string;
  lambdaName?: string;
  userId?: string;
  flowId?: string;
  status?: string;
  from?: string;
  to?: string;
  order?: string;
  q?: string; // client-side text search within the loaded rows
};

export const filtersFromParams = (params: URLSearchParams): CaptureFilters => {
  const out: CaptureFilters = {};
  for (const k of ["level", "source", "lambdaName", "userId", "flowId", "status", "from", "to", "order", "q"] as const) {
    const v = params.get(k);
    if (v) out[k] = v;
  }
  return out;
};

const toLocalInput = (iso?: string) => (iso ? new Date(iso).toISOString().slice(0, 16) : "");
const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : undefined);

export const Filters = ({ value, onChange }: { value: CaptureFilters; onChange: (next: CaptureFilters) => void }) => {
  const levels = new Set((value.level ?? "").split(",").filter(Boolean));
  const toggleLevel = (l: Level) => {
    const next = new Set(levels);
    next.has(l) ? next.delete(l) : next.add(l);
    onChange({ ...value, level: [...next].join(",") || undefined });
  };
  const text = (k: keyof CaptureFilters, label: string, placeholder?: string) => (
    <Field label={label}>
      <input className={inputClass} value={value[k] ?? ""} placeholder={placeholder} onChange={(e) => onChange({ ...value, [k]: e.target.value || undefined })} />
    </Field>
  );
  return (
    <div className="flex flex-wrap gap-3 items-end">
      <div className="flex flex-col gap-0.5 text-xs text-neutral-500">
        Level
        <div className="flex gap-1">
          {LEVELS.map((l) => (
            <button
              key={l}
              onClick={() => toggleLevel(l)}
              className={`text-xs rounded px-2 py-1 border ${levels.has(l) ? "bg-neutral-900 text-white border-neutral-900 dark:bg-neutral-100 dark:text-neutral-900" : "border-neutral-300 dark:border-neutral-700"}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
      {text("source", "Source")}
      {text("lambdaName", "Lambda")}
      {text("userId", "User id")}
      {text("flowId", "Flow id")}
      <Field label="Status">
        <select className={inputClass} value={value.status ?? ""} onChange={(e) => onChange({ ...value, status: e.target.value || undefined })}>
          <option value="">any</option>
          <option value="open">open</option>
          <option value="resolved">resolved</option>
          <option value="ignored">ignored</option>
        </select>
      </Field>
      <Field label="From">
        <input type="datetime-local" className={inputClass} value={toLocalInput(value.from)} onChange={(e) => onChange({ ...value, from: fromLocalInput(e.target.value) })} />
      </Field>
      <Field label="To">
        <input type="datetime-local" className={inputClass} value={toLocalInput(value.to)} onChange={(e) => onChange({ ...value, to: fromLocalInput(e.target.value) })} />
      </Field>
      <Field label="Order">
        <select className={inputClass} value={value.order ?? "desc"} onChange={(e) => onChange({ ...value, order: e.target.value === "asc" ? "asc" : undefined })}>
          <option value="desc">newest first</option>
          <option value="asc">oldest first</option>
        </select>
      </Field>
      {text("q", "Search loaded", "message, stack, user…")}
      <button className="text-xs text-neutral-500 underline pb-1.5" onClick={() => onChange({})}>clear</button>
    </div>
  );
};
